class Access {
	static TIMELINE = 1; // not used - timeline access is set on member record
	static LAYER = 2;
	static RESOURCE = 3;
	static GRAPHIC = 4;
	static ACHIEVEMENT = 5; // will be removed in the future
	static EMOTIONS = 6; // not used - feeling access is admin only
	static PIN = 7;

	static VIEW = 1;
	static EDIT = 2;

	static LABELS = {
		[this.LAYER]: [_('View'),_('Schedule')],
		[this.RESOURCE]: [_('View'),_('Edit')],
		[this.GRAPHIC]: [_('View'),_('Edit')],
		[this.PIN]: [_('View'),_('Place')]
	};

	constructor (type) {
		this.thingType = type;
		this.thingId = null;

		this.units = [];
		this.callback = null;
		this.buttons = new Map();
		this.unitCount = 0;

		this.popup = null;
		this._body = null;
	}

	get title() {
		switch (this.thingType) {
			case Access.LAYER: return _('Layer Access');
			case Access.RESOURCE: return _('Resource Access');
			case Access.GRAPHIC: return _('Graphic Access');
			case Access.ACHIEVEMENT: return _('Achievement Access');
			case Access.PIN: return _('Pins Access');
			default: return _('Access');
		}
	}

	get endPoint() {
		switch (this.thingType) {
			case Access.LAYER: return 'layers';
			case Access.RESOURCE: return 'items';
			case Access.GRAPHIC: return 'graphics';
			case Access.PIN: return 'pins';
		}
	}

	button(thingId, callback) {
		let viewDot = UI.div('access-dot');
		let editDot = UI.div('access-dot');
		let entry = { viewDot, editDot, thingId };
		let btn = UI.htm(
			'button',
			'ed-pri-i-btn px-2 py-1 leading-none relative',
			[UI.icon('eyes', 18), viewDot, editDot],
			{
				'mousedown': ev => stopEv(ev),
				'click': ev => this.open(entry.thingId, ev.currentTarget, callback)
			}
		);
		entry.btn = btn;
		this.buttons.set(thingId, entry);
		this._setDotState(viewDot, 0);
		this._setDotState(editDot, 0);
		return btn;
	}

	updateButtonId(oldId, newId) {
		let entry = this.buttons.get(oldId);
		if (entry) {
			this.buttons.delete(oldId);
			entry.thingId = newId;
			this.buttons.set(newId, entry);
		}
	}

	setSummary(thingId, viewCount, editCount, unitCount) {
		let entry = this.buttons.get(thingId);
		if (!entry) return;
		this._setDotState(entry.viewDot, viewCount, unitCount);
		this._setDotState(entry.editDot, editCount, unitCount);
	}

	_setDotState(dot, count, unitCount) {
		dot.className = 'access-dot';
		if (!count || !unitCount) {
			dot.classList.add('access-dot-none');
		} else if (count >= unitCount) {
			dot.classList.add('access-dot-full');
		} else {
			dot.classList.add('access-dot-partial');
		}
	}

	updateDots() {
		if (!this.thingId) return;
		let entry = this.buttons.get(this.thingId);
		if (!entry) return;

		let viewCount = 0, editCount = 0, unitCount = 0;
		for (let u of this.units) {
			unitCount++;
			let effectiveView = u.view || (u._parent && u._parent.view);
			let effectiveEdit = u.edit || (u._parent && u._parent.edit);
			if (effectiveView) viewCount++;
			if (effectiveEdit) editCount++;
		}
		this._setDotState(entry.viewDot, viewCount, unitCount);
		this._setDotState(entry.editDot, editCount, unitCount);
	}

	open(thingId, button, callback) {
		if (this.popup && this.popup._alignedTo === button) return;

		if (typeof callback != 'undefined') this.callback = callback;
		else this.callback = null;

		this.thingId = thingId;
		this.units = [];

		this._ensurePopup();
		this._body.innerHTML = '';
		this.popup.handle(button);

		API.fetch(`${this.endPoint}/${this.thingId}/access`)
		.then(reply => {
			if (reply.units) for (let data of reply.units) {
				let unit = new AccessUnit(data, this);
				this.units.push(unit);
				if (unit.isTeam) {
					this.getUnit(unit.parentId).addChild(unit);
				}
				this._body.appendChild(unit.elem);
			}

			if (reply.access) for (let data of reply.access) {
				this.getUnit(data.entity_id).init(data, reply.allowEdit);
			}

			this.update();
			this.updateDots();
		});
	}

	getUnit(id) {
		for (let u of this.units) {
			if (u.id == id) return u;
		}
		return false;
	}

	_ensurePopup() {
		if (this.popup == null) {
			this._body = UI.div();
			this.popup = Dialog.Popup(
				UI.div('p-3 min-w-64', [
					UI.div('flex gap-3 pb-1 items-center', [
						UI.div('flex-0 w-5', ''),
						UI.div('flex-1', ''),
						UI.span('flex-0 w-12 text-center text-xs text-neutral-500', Access.LABELS[this.thingType][0]),
						UI.span('flex-0 w-12 text-center text-xs text-neutral-500', Access.LABELS[this.thingType][1])
					]),
					this._body
				])
			).sticky();
		}
	}

	update() {
		this.units.forEach(u => u.update());
	}
}

class AccessUnit {
	static CIRCLE = 1;
	static COMMUNITY = 2;
	static TEAM = 3;

	constructor(data, controller) {
		this.id = data.unit_id;
		this._parentId = data.parent_id;
		this.controller = controller;

		this._elem = null;
		this._parent = this._parentId ? controller.getUnit(this._parentId) : false;

		this._editEnabled = false;
		this._prevView = this._view = this._orView = 0;
		this._prevEdit = this._edit = this._orEdit = 0;

		this.own = data.own;

		switch (data.unit_type) {
			case Nav.CIRCLE:
				this.unitDescriptor = 'Circle';
				this.icon = 'users-all';
				this.label = _('All circle members');
				break;
			case Nav.COMMUNITY:
				this.unitDescriptor = 'Community';
				this.icon = 'users-all';
				this.label = _('All community members');
				break;
			default:
				this.unitDescriptor = 'Team';
				this.icon = 'users-team';
				this.label = data.unit_name;
		}

		this.viewChk = UI.chk(
			null,
			{'change': () => {
				this.view = this.viewChk.checked;
				this.controller.update();
				this.save();
			}}
		);

		this.editChk = UI.chk(
			null,
			{'change': () => {
				this.edit = this.editChk.checked;
				this.controller.update();
				this.save();
			}}
		);
	}

	get elem() {
		if (this._elem == null) {
			this._elem = UI.div(
				'py-1 flex gap-3 items-center border-t border-neutral-200',
				[
					UI.div('flex-0', UI.icon(this.icon, 20)),
					UI.div('flex-1 text-sm', this.label),
					UI.div('flex-0 w-12 text-center', this.viewChk),
					UI.div('flex-0 w-12 text-center', this.editChk)
				]
			);
		}
		return this._elem;
	}

	get isTeam() {
		return this.sharing == false;
	}

	get parentId () {
		return this._parentId;
	}

	get view() {
		return this._view;
	}

	set view(v) {
		if (!v) this._edit = 0;
		this._view = v;
	}

	get edit() {
		return this._edit;
	}

	set edit(v) {
		if (v) this._view = 1;
		this._edit = v;
	}

	get prev() {
		if (this._prevEdit) return 2;
		if (this._prevView) return 1;
		return 0;
	}

	get value() {
		if (this._edit) return 2;
		if (this._view) return 1;
		return 0;
	}

	get values() {
		return {
			id: this.id,
			accessMode: this.value
		}
	}

	get changed() {
		return this.value != this.prev;
	}

	get editEnabled() {
		if (this._parent) return this._parent.editEnabled;
		return this._editEnabled;
	}
	set editEnabled(v) {
		this.accessRow.editDisabled = !v;
	}

	addChild(unit) {
		this.children.push(unit);
	}

	init(data, allowEdit) {
		this._prevView = this._view = (data.access_mode >= Access.VIEW) ? 1 : 0;
		this._prevEdit = this._edit = (data.access_mode >= Access.EDIT) ? 1 : 0;
		this._editEnabled = data.share_mode >= Sharing.EDIT || allowEdit;
	}

	update() {
		this.viewChk.checked = this._view || (this._parent && this._parent.view);
		this.viewChk.disabled = this._parent && this._parent.view;
		this.editChk.checked = this.edit || (this._parent && this._parent.edit);
		this.editChk.disabled = this.editDisabled || (this._parent && this._parent.edit);
	}

	save() {
		let prevView = this._prevView;
		let prevEdit = this._prevEdit;

		this._prevView = this._view;
		this._prevEdit = this._edit;

		API.insert(
			`${this.controller.endPoint}/${this.controller.thingId}/access`,
			{ changes: [this.values] }
		)
		.then(reply => {
			if (showError(reply)) {
				this._prevView = this._view = prevView;
				this._prevEdit = this._edit = prevEdit;
				this.controller.update();
				this.controller.updateDots();
				return;
			}
			if (reply.toast) makeToast(reply.toast);
			this.controller.updateDots();

			if (this.controller.callback) {
				let accessCount = 0;
				for (let u of this.controller.units) {
					if (u.value) accessCount++;
				}
				this.controller.callback(accessCount);
			}
		});
	}
}
